import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Icon, Can, Button, Spinner, colors } from '@iq/react-components';
import { useDispatch } from 'react-redux';
import { NavLink } from 'react-router-dom';

import StyledItem from '../../../../StyledItem';
import {
  parseTFRClassificationConfigTemplate,
  generateTFRClassificationConfigTemplate,
} from './utils';
import { displayNotification, getNotification } from '../../../../../bundles/notifications';
import { uploadTFRClassificationConfig } from '../../../../../bundles/sources';

const TFRClassificationItem = ({ site, source, sourcesEnabled, setSourcesEnabled, hasError }) => {
  const [isImportingConfig, setIsImportingConfig] = useState(false);
  const configInputReference = useRef(null);
  const dispatch = useDispatch();

  const { importedFileName, enabled: tfrClassificationEnabled } =
    source.options.analytics?.tfrClassification || {};
  const { dataSyncEnabled } = source.options;

  const onConfigFileChange = async (event) => {
    const [file] = event.target.files;
    if (file) {
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
      ];
      const allowedExtensions = ['.xlsx', '.xls'];

      const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
      const isValidType = allowedTypes.includes(file.type);
      const isValidExtension = allowedExtensions.includes(fileExtension);

      if (!isValidType && !isValidExtension) {
        dispatch(
          displayNotification(
            getNotification(
              'uploadTFRClassificationConfig',
              'error'
            )(['Invalid file type. Only Excel files (.xlsx, .xls) are allowed.'])
          )
        );
        return;
      }

      setIsImportingConfig(true);
      const { fileName, parsedConfig, errors } = await parseTFRClassificationConfigTemplate(file);
      if (errors.length > 0) {
        dispatch(
          displayNotification(getNotification('uploadTFRClassificationConfig', 'error')(errors))
        );
      } else {
        dispatch(uploadTFRClassificationConfig(site.id, source.id, parsedConfig, fileName));
      }
      setIsImportingConfig(false);
    }
  };

  const openConfigFileInput = () => {
    configInputReference.current.value = '';
    configInputReference.current.click();
  };

  const toggleEnable = (event) => {
    const newSourcesEnabled = { ...sourcesEnabled, [source.id]: event.target.checked };
    setSourcesEnabled(newSourcesEnabled);
  };

  return (
    <div
      className="tfr-classification-source"
      key={source.id}
    >
      <div className="source-header">
        <div className="source-name">{source.name}</div>
        <Can
          permission="sources/Write"
          scope={{ org: site.org, site: site.id }}
        >
          <input
            type="file"
            accept=".xlsx"
            style={{ display: 'none' }}
            ref={configInputReference}
            onChange={(event) => onConfigFileChange(event)}
          />
          <Button
            className="tfr-upload-button"
            activity="secondary"
            onClick={openConfigFileInput}
            disabled={isImportingConfig}
            tooltip="Upload TFR configuration file"
          >
            {isImportingConfig ? (
              <Spinner />
            ) : (
              <>
                <Icon
                  size="l"
                  icon="he-upload"
                />
                Upload Config
              </>
            )}
          </Button>
        </Can>
      </div>
      <div className="source-body">
        <div>
          <div className="tfr-classification-enable">
            <input
              checked={sourcesEnabled[source.id]}
              id={`enable-${source.id}`}
              type="checkbox"
              onChange={toggleEnable}
            />
            <label htmlFor={`enable-${source.id}`}>Enable TFR classification</label>
          </div>
          {tfrClassificationEnabled && dataSyncEnabled === false && (
            <div className="tfr-classification-datasync-warning">
              <span className="tfr-classification-error">*</span> Data sync is disabled for this
              source
            </div>
          )}
        </div>
        <div className="tfr-classification-file-info">
          {importedFileName && (
            <div className="tfr-classification-file-name">
              <Icon
                icon="he-systemfeedbacksuccess"
                size="s"
                style={{ color: colors.StatusGreen }}
              />
              {importedFileName}
            </div>
          )}
          {hasError && !importedFileName && (
            <div className="tfr-classification-error">No configuration file uploaded</div>
          )}
        </div>
      </div>
    </div>
  );
};

const TfrClassification = ({ site = {}, sources, onUpdate }) => {
  const [isOpen, setOpen] = useState(sources.length === 0);
  const [sourcesEnabled, setSourcesEnabled] = useState({});
  const [errorSourceIds, setErrorSourceIds] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    const newSourcesEnabled = { ...sourcesEnabled };
    for (const source of sources) {
      newSourcesEnabled[source.id] = source.options.analytics?.tfrClassification?.enabled || false;
    }
    setSourcesEnabled(newSourcesEnabled);
  }, [sources]);

  const updateSources = useCallback(() => {
    const sourcesWithErrors = [];

    for (const sourceId of Object.keys(sourcesEnabled)) {
      const source = sources.find((s) => s.id === sourceId);
      if (!source) continue;

      // Check if trying to enable without config file
      const hasConfigFile = source.options.analytics?.tfrClassification?.importedFileName;
      if (sourcesEnabled[sourceId] && !hasConfigFile) {
        sourcesWithErrors.push(sourceId);
        continue;
      }

      // Skip if no change in enabled state
      if (
        (source.options.analytics?.tfrClassification?.enabled || false) === sourcesEnabled[sourceId]
      ) {
        continue;
      }

      const updatedSource = {
        ...source,
        options: {
          ...source.options,
          analytics: {
            ...source.options.analytics,
            tfrClassification: {
              ...source.options?.analytics?.tfrClassification,
              enabled: sourcesEnabled[sourceId],
            },
          },
        },
      };

      // Remove sensitive data before update
      if (updatedSource.options.clientSecret !== undefined) {
        const { clientSecret, ...restOptions } = updatedSource.options;
        updatedSource.options = restOptions;
      }

      onUpdate(sourceId, updatedSource);
    }

    if (sourcesWithErrors.length > 0) {
      setErrorSourceIds(sourcesWithErrors);
      dispatch(
        displayNotification(
          getNotification(
            'uploadTFRClassificationConfig',
            'error'
          )(['Cannot enable TFR classification without uploading a configuration file first.'])
        )
      );
    } else {
      setErrorSourceIds([]);
    }
  }, [sourcesEnabled, sources, onUpdate, dispatch]);

  return (
    <StyledItem
      itemClass="TFR-classification"
      active={isOpen}
      onClick={() => sources.length > 0 && setOpen(!isOpen)}
      headerContent={
        <div className="title">
          TFR classification settings
          <Button
            design="text"
            slim
            icon={
              <Icon
                icon="he-info"
                size="s"
              />
            }
            tooltip={`Use this setting to upload a configuration file and choose whether the system should process and classify TFR files based on it.`}
            onClick={(event) => event.preventDefault()}
          ></Button>
        </div>
      }
      headerActions={
        <>
          <Button
            className="template-download"
            design="text"
            activity="secondary"
            onClick={(event) => {
              event.stopPropagation();
              generateTFRClassificationConfigTemplate(site);
            }}
          >
            <Icon icon="he-download" />
            Download TFR config template
          </Button>
          {sources.length > 0 ? <Icon icon={isOpen ? 'he-up' : 'he-down'} /> : null}
        </>
      }
      bodyActionsPosition="center"
    >
      {sources.map((source) => (
        <TFRClassificationItem
          site={site}
          sourcesEnabled={sourcesEnabled}
          setSourcesEnabled={setSourcesEnabled}
          source={source}
          key={source.id}
          hasError={errorSourceIds.includes(source.id)}
        />
      ))}
      {sources.length > 0 ? null : (
        <div className="no-sources-div">
          There are currently no EC sources configured. Please{' '}
          <Can
            permission="sources/Write"
            scope={{ org: site.org, site: site.id }}
          >
            <NavLink
              exact
              to={{
                pathname: `/sites/${site.id}/admin/sources/`,
                search: globalThis.location.search,
              }}
            >
              click here
            </NavLink>
          </Can>{' '}
          to configure them first.
        </div>
      )}
      {sources.length > 0 ? (
        <div className="tfr-classification-footer">
          {sources.length > 0 ? <Button onClick={updateSources}>Save</Button> : null}
        </div>
      ) : null}
    </StyledItem>
  );
};

export default TfrClassification;
